import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsLabelsForMethod } from '../analytics/labels';
import {
  emitPaymentAnalytics,
  PaymentAnalyticsSession,
  type PaymentAnalyticsContext,
} from '../analytics/paymentAnalytics';
import type { PaymentMethodItem } from '../components/PaymentMethodCarousel/types';
import { CHECKOUT_HOST_MESSAGE_SOURCE } from './reportFrameHeight';
import { installHostAnalyticsTransport, notifyAnalyticsReady } from './postAnalytics';

function readInitAnalytics(): PaymentAnalyticsContext | null {
  const init = (window as Window & { __INIT__?: { analytics?: PaymentAnalyticsContext } }).__INIT__;
  return init?.analytics ?? null;
}

export function usePaymentAnalytics(params: {
  paymentMethods: PaymentMethodItem[];
  selectedMethodId?: string;
}): {
  onUserSelect: (id: string) => void;
  onSubmit: () => void;
  onEnableSplit: () => void;
  onGiftCodes: (codes: string[]) => void;
} {
  const sessionRef = useRef<PaymentAnalyticsSession | null>(null);
  if (!sessionRef.current) {
    sessionRef.current = new PaymentAnalyticsSession(emitPaymentAnalytics);
  }

  const methodsRef = useRef(params.paymentMethods);
  methodsRef.current = params.paymentMethods;

  const [context, setContext] = useState<PaymentAnalyticsContext | null>(readInitAnalytics);

  useEffect(() => {
    installHostAnalyticsTransport();
    notifyAnalyticsReady();
    const retry = window.setTimeout(notifyAnalyticsReady, 400);
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as {
        source?: string;
        type?: string;
        context?: PaymentAnalyticsContext;
      } | null;
      if (!data || data.source !== CHECKOUT_HOST_MESSAGE_SOURCE) return;
      if (data.type !== 'analytics-context' || !data.context) return;
      setContext(data.context);
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.clearTimeout(retry);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    if (context) sessionRef.current?.setContext(context);
  }, [context]);

  useEffect(() => {
    if (!params.selectedMethodId) return;
    const labels = analyticsLabelsForMethod(methodsRef.current, params.selectedMethodId);
    if (!labels) return;
    sessionRef.current?.noteInitialMethod(labels.payment_type);
  }, [params.selectedMethodId, params.paymentMethods]);

  const onUserSelect = useCallback((id: string) => {
    const labels = analyticsLabelsForMethod(methodsRef.current, id);
    if (!labels) return;
    sessionRef.current?.selectMethod(labels.payment_type, labels.payment_box, labels.payment_issuer);
  }, []);

  const onSubmit = useCallback(() => {
    sessionRef.current?.click('button', 'finaliza_compra');
  }, []);

  const onEnableSplit = useCallback(() => {
    sessionRef.current?.click('button', 'pagar_con_2_tarjetas');
  }, []);

  const onGiftCodes = useCallback((codes: string[]) => {
    for (const code of codes) sessionRef.current?.giftCode(code);
  }, []);

  return { onUserSelect, onSubmit, onEnableSplit, onGiftCodes };
}
